/** Reviewed internal references. Account IDs/numbers, bodies and text are not admitted. */
export const INTERNAL_ID_FIELDS = [
  'client_guid',
  'client_id',
  'case_id',
  'source_case_id',
  'target_case_id',
  'action_id',
  'option_id',
  'outcome_id',
  'goal_id',
  'conversation_id',
  'ui_session_id',
  'run_id',
  'message_id',
] as const;

export function sentryClientGuid(value: unknown): string | undefined {
  return typeof value === 'string' &&
    /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(value)
    ? value.toLowerCase()
    : undefined;
}

export function telemetryInternalIds(
  input: Record<string, unknown> = {},
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of INTERNAL_ID_FIELDS) {
    const raw = input[key];
    const value =
      typeof raw === 'number' && Number.isSafeInteger(raw) && raw >= 0
        ? String(raw)
        : raw;
    if (key === 'client_guid') {
      const guid = sentryClientGuid(value);
      if (guid) result[key] = guid;
    } else if (
      typeof value === 'string' &&
      /^[a-z\d][a-z\d_.-]{0,127}$/i.test(value)
    ) {
      result[key] = value;
    }
  }
  return result;
}

/** Extract reviewed references only from known resource routes; never arbitrary URL/query data. */
export function telemetryIdsFromUrl(value: unknown): Record<string, string> {
  if (typeof value !== 'string') return {};
  try {
    const url = new URL(value, 'https://route.invalid');
    const path = url.pathname;
    if (path === '/v1/prototype/client/metadata')
      return telemetryInternalIds({
        client_guid: url.searchParams.get('guid'),
      });
    const caseMatch =
      /^\/case\/([^/]+)(?:\/option(?:\/([^/]+))?)?(?:\/transition)?\/?$/.exec(
        path,
      );
    if (caseMatch)
      return telemetryInternalIds({
        case_id: caseMatch[1],
        option_id: caseMatch[2],
      });
    const resource =
      /^\/(action|goal|outcome)\/([^/]+)(?:\/(?:link|unlink|invalidate))?\/?$/.exec(
        path,
      );
    if (resource)
      return telemetryInternalIds({ [`${resource[1]}_id`]: resource[2] });
    const session = /^\/ui-session\/([^/]+)\/?$/.exec(path);
    if (session) return telemetryInternalIds({ ui_session_id: session[1] });
    const conversation =
      /^(?:\/api\/v1)?\/conversations\/([^/]+)(?:\/(messages|stream|runs)(?:\/([^/]+))?)?\/?$/.exec(
        path,
      );
    if (conversation)
      return telemetryInternalIds({
        conversation_id: conversation[1],
        run_id:
          conversation[2] === 'runs'
            ? conversation[3]
            : conversation[2] === 'stream'
              ? url.searchParams.get('run_id')
              : undefined,
      });
  } catch {
    /* Invalid URLs provide no identifier context. */
  }
  return {};
}

export function sentryClientGuidFromUrl(value: unknown): string | undefined {
  return telemetryIdsFromUrl(value)['client_guid'];
}
